import api from '@/services/api'

export type ReferralPublicStatus =
  | 'recebido'
  | 'em_contato'
  | 'reuniao'
  | 'proposta'
  | 'ganho'
  | 'perdido'
  | 'comissao_pendente'
  | 'comissao_liberada'
  | 'pago'

export type PortalReferral = {
  id: string
  empresa: string
  createdAt: string
  publicStatus: ReferralPublicStatus
  nome: string | null
  telefone: string | null
  email: string | null
  site: string | null
  observacao: string | null
  canEdit: boolean
  contratoMensal: number | null
  fechadoEm: string | null
  commission: { status: string; valor: number | null } | null
  comissaoPrevista: { valor: number; recorrente: boolean } | null
}

export type PortalLeadPage = {
  data: PortalReferral[]
  page: number
  limit: number
  total: number
  totalPages: number
  byStatus?: Partial<Record<ReferralPublicStatus, number>>
}

export type PartnerPromotion = {
  id: string
  title: string
  rewardName: string
  description: string
  bannerText: string | null
  imageUrl: string | null
  targetValue: number
  startDate: string
  endDate: string
  isActive: boolean
  partnershipType: string | null
}

export type PartnerPromotionPayload = Omit<PartnerPromotion, 'id'> & { id?: string }

export const STATUS_LABEL: Record<ReferralPublicStatus, string> = {
  recebido: 'Recebido',
  em_contato: 'Em contato',
  reuniao: 'Reunião',
  proposta: 'Proposta',
  ganho: 'Fechado',
  perdido: 'Não avançou',
  comissao_pendente: 'Comissão pendente',
  comissao_liberada: 'Comissão liberada',
  pago: 'Pago',
}

export const referralsService = {
  register(payload: {
    nome: string
    empresa: string
    telefone: string
    email: string
    aceitouTermos: true
    closer?: string
    idioma?: string
    fusoHorario?: string
    latitude?: number
    longitude?: number
  }) {
    return api.post('/referrals/register', payload).then((r) => r.data)
  },
  previewConfirmacao(token: string) {
    return api.get('/referrals/cadastro/confirmacao', { params: { token } }).then((r) => r.data.data)
  },
  confirmarCadastro(payload: {
    token: string
    nome: string
    empresa?: string
    telefone: string
    cnpj?: string
    responsavel?: string
    telefoneContato?: string
    aceitouTermos: true
    idioma?: string
    fusoHorario?: string
    latitude?: number
    longitude?: number
  }) {
    return api.post('/referrals/cadastro/confirmacao', payload).then((r) => r.data)
  },
  invite(payload: {
    tipo: 'AGENCIA' | 'PLATAFORMA' | 'INDICADOR' | 'OUTRO'
    contato: string
    empresa: string
    telefone: string
    email: string
    cnpj?: string
    site?: string
  }) {
    return api.post('/referrals/invites', payload).then((r) => r.data)
  },
  acceptInvite(token: string, password: string) {
    return api.post('/referrals/auth/accept', { token, password }).then((r) => r.data)
  },
  login(email: string, password: string) {
    return api.post('/referrals/auth/login', { email, password }).then((r) => r.data)
  },
  verifyOtp(email: string, code: string) {
    return api.post('/referrals/auth/verify-otp', { email, code }).then((r) => r.data)
  },
  listMine(page = 1, limit = 20) {
    return api
      .get<{ success: true; data: PortalLeadPage }>('/referrals/me/leads', { params: { page, limit } })
      .then((r) => r.data.data)
  },
  importMine(file: File) {
    const body = new FormData()
    body.append('file', file)
    return api
      .post<{ success: true; data: { imported: number; failed: { line: number; nome?: string; message: string }[]; repeated?: number } }>(
        '/referrals/me/leads/import',
        body,
        { timeout: 600000 },
      )
      .then((r) => r.data.data)
  },
  importStaff(file: File, parceiroId: string) {
    const body = new FormData()
    body.append('file', file)
    body.append('parceiroId', parceiroId)
    return api
      .post<{ success: true; data: { imported: number; failed: { line: number; nome?: string; message: string }[]; repeated?: number } }>(
        '/referrals/staff/leads/import',
        body,
        { timeout: 600000 },
      )
      .then((r) => r.data.data)
  },
  promotionParticipants() {
    return api
      .get<{
        success: true
        data: {
          promotion: PartnerPromotion | null
          participants: Array<{
            parceiroId: string
            nome: string
            empresa: string | null
            tipo: string | null
            indicacoes: number
            valorFechado: number
            meta: number
            pct: number
          }>
        }
      }>('/referrals/staff/promotions/participants')
      .then((r) => r.data.data)
  },
  createMine(payload: {
    nome: string
    empresa: string
    telefone?: string
    email?: string
    site?: string
    observacao?: string
  }) {
    return api.post('/referrals/me/leads', payload).then((r) => r.data)
  },
  updateMine(
    id: string,
    payload: {
      nome: string
      empresa: string
      telefone?: string
      email?: string
      site?: string
      observacao?: string
    },
  ) {
    return api.patch(`/referrals/me/leads/${id}`, payload).then((r) => r.data)
  },
  listStaff(page = 1, limit = 200) {
    return api.get('/referrals/staff/leads', { params: { page, limit } }).then((r) => r.data)
  },
  sendToCarteira(id: string) {
    return api
      .post<{ success: true; data: { referralId: string; leadInstanceId: string; already: boolean } }>(
        `/referrals/staff/leads/${id}/carteira`,
      )
      .then((r) => r.data.data)
  },
  discardStaff(id: string) {
    return api
      .post<{ success: true; data: { referralId: string; leadInstanceId: string | null } }>(
        `/referrals/staff/leads/${id}/descartar`,
      )
      .then((r) => r.data.data)
  },
  createStaff(payload: { nome: string; empresa: string; telefone?: string; email?: string; site?: string; parceiroId: string }) {
    return api.post('/referrals/staff/leads', payload).then((r) => r.data)
  },
  listNfs() {
    return api.get('/referrals/me/nfs').then((r) => r.data)
  },
  submitNf(comissaoId: string, nfFileUrl: string, numeroNf?: string) {
    return api.post('/referrals/me/nfs', { comissaoId, nfFileUrl, numeroNf }).then((r) => r.data)
  },
  activePromotion() {
    return api
      .get<{ success: true; data: PartnerPromotion | null }>('/referrals/me/promotions/active')
      .then((r) => r.data.data)
  },
  listMyPromotions() {
    return api
      .get<{ success: true; data: PartnerPromotion[] }>('/referrals/me/promotions')
      .then((r) => r.data.data)
  },
  listPromotions() {
    return api
      .get<{ success: true; data: PartnerPromotion[] }>('/referrals/staff/promotions')
      .then((r) => r.data.data)
  },
  savePromotion(payload: PartnerPromotionPayload) {
    const body = { ...payload }
    delete body.id
    const request = payload.id
      ? api.patch(`/referrals/staff/promotions/${payload.id}`, body)
      : api.post('/referrals/staff/promotions', body)
    return request.then((r) => r.data)
  },
  uploadPromotionBanner(file: File) {
    const formData = new FormData()
    formData.append('banner', file)
    return api
      .post<{ success: true; data: { imageUrl: string } }>('/referrals/staff/promotions/banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data.data)
  },
}
